import React, { FunctionComponent, memo, useMemo } from "react";
import { useIntl } from "react-intl";
import { zip, sum, isEqual } from "lodash";

import { ElectroPrices, PredictionData } from "./types";

import GroupHeading from "./GroupHeading";
import RowOfValues from "./RowOfValues";



interface IProps {
  cellWidth?: number;
  cellPaddingX?: number;
  data: Record<string, any>;
}


const RowsOfValues: FunctionComponent<IProps> = memo(function RowsOfValues({
  cellWidth = 35,
  cellPaddingX = 0,
  data,
}) {
  const intl = useIntl();
  const prediction: PredictionData = data.prediction;
  const e_prices: ElectroPrices = data.e_prices;
  const nowHour = useMemo<number>(() => prediction?.hour || 0, [prediction?.hour]);
  const nowHourIndex = useMemo<number>(() => prediction?.hours?.indexOf(nowHour), [nowHour, prediction?.hours]);

  const consumptionPrediction = useMemo(() => prediction ? zip(prediction.predicted_consumption, prediction.predicted_consumption_heating).map(sum) : [], [prediction]);

  const commonRowProps = useMemo(() => ({
    cellWidth,
    cellPaddingX,
    nowHourIndex,
  }), [cellWidth, cellPaddingX, nowHourIndex]);

  const goodBadColors = useMemo(() => ({
    // colors: ["#3ab860", "#E309"],
    colors: ["#3ab860", "transparent"],
  }), []);

  const badGoodColors = useMemo(() => ({
    // colors: ["#E309", "#3ab860"],
    colors: ["transparent", "#3ab860"],
  }), []);

  return (
    <>
      {prediction && <>
        {e_prices && <>
          <GroupHeading>
            {intl.formatMessage({
              id: "site__trading__electricity_prices__heading",
              defaultMessage: "Electricity Prices",
            })}
          </GroupHeading>
          <RowOfValues {...commonRowProps} {...goodBadColors} key="ey" label={intl.formatMessage({id: "site__trading__row__buy", defaultMessage: "buy"})} units="€/MWh" values={e_prices.buy_kwh} />
          <RowOfValues {...commonRowProps} key="eb" label={intl.formatMessage({id: "site__trading__row__base", defaultMessage: "base"})} units="€/MWh" values={e_prices.base_kwh} withoutChart />
          <RowOfValues {...commonRowProps} key="es" label={intl.formatMessage({id: "site__trading__row__sell", defaultMessage: "sell"})} units="€/MWh" values={e_prices.sell_kwh} withoutChart />
        </>}

        <GroupHeading>
          {intl.formatMessage({
            id: "site__trading__prediction__heading",
            defaultMessage: "Prediction",
          })}
        </GroupHeading>
        <RowOfValues {...commonRowProps} {...goodBadColors} key="cs" label={intl.formatMessage({id: "site__trading__row__clouds", defaultMessage: "clouds"})} units="%" values={prediction.weather.map(_ => (1 - _) * 100)} />
        <RowOfValues {...commonRowProps} {...badGoodColors} key="pp" label={intl.formatMessage({id: "site__trading__row__production__pv", defaultMessage: "production PV"})} units="kWh" values={prediction.predicted_production_pv} corrections={prediction.real_production_pv} />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="et" label={intl.formatMessage({id: "site__trading__row__carbon_emissions", defaultMessage: "CO<sub>2</sub> emissions"})} units="kg/kWh" values={prediction.predicted_carbon_emissions} corrections={prediction.real_carbon_emissions} multiplier={0.001} />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="es" label={intl.formatMessage({id: "site__trading__row__carbon_savings", defaultMessage: "CO<sub>2</sub> savings"})} units="kg/kWh" values={prediction.predicted_carbon_savings} corrections={prediction.real_carbon_savings} multiplier={0.001} />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="co" label={intl.formatMessage({id: "site__trading__row__consumption", defaultMessage: "consumption"})} units="kWh" values={consumptionPrediction} corrections={prediction.real_consumption} />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="pd" label={intl.formatMessage({id: "site__trading__row__power_draw", defaultMessage: "grid power draw"})} units="kWh" values={prediction.predicted_power_draw} corrections={prediction.real_power_draw} />
      </>}
    </>
  );
}, isEqual);

export default RowsOfValues;
